import { z } from 'zod/v4';
import { FindingResourceSchema } from './finding-resource.js';

export const LIST_FINDINGS_DEFAULT_PAGE_SIZE = 10;

/**
 * The largest page the findings lists will serve. A bigger `count` is
 * **clamped**, not rejected — the four handlers have always capped it at 100 and
 * answered 200, so a 400 here would break callers that ask for more.
 */
export const LIST_FINDINGS_MAX_PAGE_SIZE = 100;

/**
 * The findings list query, shared by all four list endpoints.
 *
 * `filter` is a JSON string, silently ignored when it does not parse — that is
 * the behaviour of every CAPA list and callers rely on it. The keys it accepts:
 *
 * | key | matches |
 * | --- | --- |
 * | `ids` | `af.id IN (…)`, an array |
 * | `title`, `description` | case-insensitive `LIKE` |
 * | `urgency`, `source`, `type`, `impactAreaId`, `assigneeUserId` | exact |
 * | `status` | the **derived** status, applied in an outer query |
 * | `assignee` | the assignee's name or email, case-insensitive `LIKE` |
 * | `dueDateFrom`, `dueDateTo` | inclusive range on `due_date` |
 * | `createdAt` | the whole UTC day |
 * | `isOverdue` | `true` → `due_date` in the past |
 * | `tags` | a comma-separated tag-id list, AND semantics |
 * | `isLegal` | whether `additionalInformation.flag` is set — SLCP legal issues |
 * | `slcpQuestionId` | an SLCP finding's `additionalInformation.questionId` |
 * | `companyId` | the finding's owning company — **network scope only** |
 *
 * `isLegal` and `slcpQuestionId` exist because the SLCP assessment views used to
 * reach the legacy CAPA v1 service for exactly those two queries; both scopes
 * accept them so a facility and a buyer can ask the same question of their own
 * endpoint.
 */
export const ListFindingsQuerySchema = z.object({
  count: z
    .preprocess(
      val => val ?? undefined,
      z.coerce.number().int().min(1).optional(),
    )
    .default(LIST_FINDINGS_DEFAULT_PAGE_SIZE)
    .transform(value => Math.min(value, LIST_FINDINGS_MAX_PAGE_SIZE)),
  page: z
    .preprocess(
      val => val ?? undefined,
      z.coerce.number().int().min(0).optional(),
    )
    .default(0),
  sort: z.string().optional(),
  filter: z
    .string()
    .max(512)
    .optional()
    .describe('JSON string with filter criteria for findings'),
  /** Narrows to one audit or SLCP assessment (`af.audit_id`). */
  auditId: z.string().optional(),
  /**
   * Sorts the findings already linked to this plan to the top and reports
   * `isLinkedToPlan` on every row. Used by the plan detail's finding picker.
   */
  capId: z.string().optional(),
});

export const ListFindingsResponseSchema = z.object({
  data: z.array(FindingResourceSchema),
  meta: z.object({
    count: z.number(),
    page: z.number(),
    pageSize: z.number(),
    totalCount: z.number(),
  }),
});

export type ListFindingsQuery = z.infer<typeof ListFindingsQuerySchema>;
export type ListFindingsResponse = z.infer<typeof ListFindingsResponseSchema>;
